import { Button, Col, Row, Space, Typography, Grid } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, Link} from "react-router-dom";
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import { useLegalDocDialog } from '../../hooks/legal-doc';
import TransactionListDisplay from "./TransactionListDisplay";
import { Coinbase_Auth_Token } from "../../const/const";
import CryptoAccountLoginStatus from "../CryptoAccountLoginStatus";
import { useAppBreakpoint } from '../../hooks/breakpoints';
import { useAuth } from "../../contexts/auth-context";
import {
  getPendingTransactionStatusMetamask,
} from '../../helpers';

import './styles/TransactionList.css';
import noTransactionsYet from "../../images/no-transactions-yet.jpg";

// GK 2026-02-23 Sign Legal Docs
// Need a check that sees when the user last signed the legal docs
// And requires that they submit them if they have not
// In order to account for the first time after registering, 
// we are going to make them sign the legal again when first logging in. 
// However, the message will be different on first sign in versus subsequent times their legal has expired

// Example:
// John signs up for puunkypay. 
// John is given a "welcome" popup where he is also required to sign the legal docs

// Timmy has an account with punkypay and has signed the legal docs
// but the legal docs have expired and new legal docs are required to sign
// Timmy must re-sign the new leagl docs, and this must be saved in the database

// The reason why we know Timmy has not signed the most recent legal docs is due to a variable like, 
// legalDocsLastUpdated = "2026-02-15"
// If legalDocsLastUpdated = "2026-02-15" and our database has record of this user signing the docs on "2026-02-10"...
// THEN this popup appears

// GK 2023-10-22 - Creates TransactionList (we put this on main page)
const TransactionList = () => {
  const storage = localStorage;
  const { Title, Text } = Typography;
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [pendingCoinbaseTransactions, setPendingCoinbaseTransactions] = useState([]);
  const [pendingMetamaskTransactions, setPendingMetamaskTransactions] = useState([]);

  const [inProgress, setInProgress] = useState(true);
  const cryptoAccount = useSelector(state => state.cryptoAccount.account);
  const isMediumScreen = useAppBreakpoint('sm');
  const auth = useAuth();
  const loginEmail = auth.currentUser?.email; //storage.getItem('email');
  const location = useLocation();
  
  useLegalDocDialog();

  //TODO: Move this to the redux state
  // GK 2023-10-22 - The below function grabs transactions from our database,
  const getTransactions = async () => {
    try {
      const response = await axios.post('/api/transactions', {
        loginEmail,
        accountId: null, //result[0]?.id,
        accessToken: storage.getItem(Coinbase_Auth_Token),
      });
  
      console.log('Mongo Transactions', response.data);
      setTransactions(response.data.transactions ?? []);
      setPendingCoinbaseTransactions(response.data.pendingCoinbaseTransactions ?? []); // used by ChronJob below
      setPendingMetamaskTransactions(response.data.pendingMetamaskTransactions ?? []);
      setInProgress(false);

    } catch (error) {
      console.log('ALL Transactions Error ->', error);
      // If the error response indicates an expired token, sign out the user
      if (error.response && error.response.data.errorCode === 'TOKEN_EXPIRED') {
        auth.signOut();
      }
      setInProgress(false);
    }
  }

  // Monitor pending transactions and update table when there is a new update
  // This is like a "ChronJob"
  const handleCoinbaseInterval = () => {
    const interval = setInterval(() => {
      if(pendingCoinbaseTransactions.length > 0) {
        getTransactions();
      }

    }, 1*60*1000); // Delay is 1 mins -> 1*60*1000

    return interval;
  }

  const processMetamaskPendingTransaction = async (pendingMetamaskTransaction) => {
    if(!pendingMetamaskTransaction.exchange_txn_id) {
      return null;
    }

    try {
      const result = await getPendingTransactionStatusMetamask(pendingMetamaskTransaction.exchange_txn_id);
      if (['completed', 'failed'].includes(result.status)) {
        // update the backend with the new status
        const response = await axios.patch(`/api/transactions/${pendingMetamaskTransaction.exchange_txn_id}`, {
          transactionStatus: result.status,
        });

        return response.data;
      }

      return null;
    } catch (error) {
      // just log the error. Don't throw error which can prevent other transactions from completing their update operation
      console.error('Error updating metamask status', error);
      return null;
    }
  }

  const handleMatamaskPendingTransactions = async () => {
    const txnResults = await Promise.all(pendingMetamaskTransactions.map((pendingMetamaskTransaction) => {
      return processMetamaskPendingTransaction(pendingMetamaskTransaction);
    }));
    
    // refetch if there is at least one update that happened
    if (txnResults.some(txnResult => txnResult !== null)) {
        getTransactions();
    }
  }
  
  const handleMetamaskInterval = () => {
    const interval = setInterval(() => {
      if (pendingMetamaskTransactions.length > 0) {
        handleMatamaskPendingTransactions();
      }

    }, 15 * 1000); // Delay is 30 seconds

    return interval;
  }

  useEffect(() => {
    console.log('Email Changed');
    if(loginEmail) {
      console.log('Email has value ****');
      getTransactions();
    }
  }, [loginEmail])

  // Coinbase ChronJob
  useEffect(() => {
    // this runs the chronjob
    const interval = handleCoinbaseInterval();

    // when you navigate away from the page, remove the chronjob
    return () => clearInterval(interval);
  }, [pendingCoinbaseTransactions]);

  // Metamask ChronJob
  useEffect(() => {
    // this runs the chronjob
    const interval = handleMetamaskInterval();

    // when you navigate away from the page, remove the chronjob
    return () => clearInterval(interval);
  }, [pendingMetamaskTransactions]);

  // GK 2023-10-22 - creates the transaction list page
  // note: the actual transaction list comes from the TransactionTable below
  return (
    <div style={{ marginTop: "10px" }}>
      <Helmet>
          <link rel="canonical" href={`https://punkypay.com${location.pathname}`} />
          <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="text-center" style={{marginBottom: '10px'}}>
        <CryptoAccountLoginStatus/>
      </div>
      <Row gutter={[16, 32]}>
        {/* <Col md={{span: 22, offset: 1}} xs={{span: 24}} className="transaction-actions-section">
          <Button
            block={!isMediumScreen}
            type="primary"
            className="btn-transaction"
            onClick={() => {
              navigate("/transaction");
            }}
          >
            Make Transaction
          </Button>
          <Button
            block={!isMediumScreen}
            type="primary"
            className="btn-transaction"
            onClick={() => {
              navigate("/crypto-login");
            }}
          >
            Sign in to Crypto Exchange
          </Button>
        </Col> */}
        {/* <Col md={{span: 22, offset: 1}} xs={{span: 24}}  >
          <TransactionTable transactions={transactions} inProgress={inProgress} />
        </Col> */}
        <Col md={{span: 22, offset: 1}} xs={{span: 24}}  >
          
        {
          transactions.length ? (
            <TransactionListDisplay
              transactions={transactions}
              inProgress={inProgress}
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: "20px",
                paddingBottom: "20px",
                backgroundColor: "#000",
              }}
            >
              <img
                src={noTransactionsYet}
                alt="No transactions yet"
                style={{
                  width: "80%",
                  maxWidth: "800px",
                  height: "auto",
                  display: "block",
                }}
              />
            </div>
          )
        }
          {/* {
            transactions.length ? 
              <TransactionListDisplay transactions={transactions} inProgress={inProgress} /> :
              <div style={{display: 'flex', justifyContent: 'center', paddingTop: '20px', paddingBottom: '20px', backgroundColor: '#2d2c2c'}}>No transactions yet</div>
          } */}
        </Col>
      </Row>
      
      <Row justify="center" gutter={[16, 14]}>
        <Col xs={{ span: 24 }} md={{ span: 8 }}>
          <p class="auth-title-text" justify="center"> Need help? Check out our <Link class="light-blue" to="/support">support page</Link>.</p>
        </Col>
      </Row>
    </div>
  );
};


export default TransactionList;
